// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductDetailsVariantOptionsContext } from "@gcg/App/Contexts/ProductDetailsVariantOptionsContext";
import ProductOptionContext from "@gcg/App/Widgets/ProductDetails/ProductOptionContext";
import productOptionsStyles from "@gcg/App/Widgets/ProductDetails/ProductOptions.style";
import Info from "@gcg/XDL/Icons/Info";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import {
    ChildTraitValueModel,
    ProductInventoryDto,
    ProductModel,
    VariantTraitModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import Pagination from "@insite/mobius/Pagination";
import Popover from "@insite/mobius/Popover";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface VariantChildContext {
    product: ProductModel;
    productInfo: ProductInfo;
}

interface ProductOptionSorting {
    type: "productNumber" | "traitId";
    asc: boolean;
    data: string;
}

interface ProductOptionPagination {
    totalItemCount: number;
    page: number;
    pageSize: number;
    pageSizeOptions: number[];
    filteredChildren: VariantChildContext[];
    visibleChildren: VariantChildContext[];
    oneImageAvailable: boolean;
    sort: ProductOptionSorting;
}

const compareStrings = (stringA: string | undefined, stringB: string | undefined, asc: boolean) => {
    let result = 0;

    if (stringA && stringB) {
        if (asc) {
            result = stringA.localeCompare(stringB);
        } else {
            result = stringB.localeCompare(stringA);
        }
    }

    return result;
};

type SortingMethod = (items: VariantChildContext[], asc: boolean, extraData?: string) => VariantChildContext[];

const sortingMethods: Dictionary<SortingMethod> = {
    productNumber: (items: VariantChildContext[], asc: boolean): VariantChildContext[] => {
        return items.sort((a: VariantChildContext, b: VariantChildContext) =>
            compareStrings(a.product.productNumber, b.product.productNumber, asc),
        );
    },
    traitId: (items: VariantChildContext[], asc: boolean, traitId?: string): VariantChildContext[] => {
        return items.sort((a: VariantChildContext, b: VariantChildContext) => {
            const valueA = a.product.childTraitValues?.find(value => value.styleTraitId === traitId);
            const valueB = b.product.childTraitValues?.find(value => value.styleTraitId === traitId);

            return compareStrings(valueA?.valueDisplay, valueB?.valueDisplay, asc);
        });
    },
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    const variantChildContexts: VariantChildContext[] = [];
    const variantChildren = getVariantChildrenDataView(state, ownProps.parentProductId).value;

    variantChildren?.every(product => {
        variantChildContexts.push({ product, productInfo: createFromProduct(product) });

        return true;
    });

    const variantTraits = getProductState(state, ownProps.parentProductId).value?.variantTraits;

    return {
        variantTraits,
        variantChildContexts: sortingMethods.productNumber(variantChildContexts, false),
        selectedVariantId: state.pages.productDetails.selectedProductId,
        settingsCollection: getSettingsCollection(state),
    };
};

const mapDispatchToProps = {
    loadRealTimePricing,
    loadRealTimeInventory,
};

const styles = productOptionsStyles;

const sortTraitValuesOfVisibleChildren = (
    variantTraits: VariantTraitModel[],
    visibleContext: VariantChildContext[],
) => {
    visibleContext.every(context => {
        const sortedChildValues: ChildTraitValueModel[] = [];

        variantTraits.every(trait => {
            const value = context.product.childTraitValues?.find(value => trait.id === value.styleTraitId);
            value && sortedChildValues.push(value);
            return true;
        });

        context.product = { ...context.product, childTraitValues: sortedChildValues };

        return true;
    });
};

const hasOneAvailableImage = (visibleChildren: VariantChildContext[]) => {
    let result = false;

    visibleChildren.every(childContext => {
        const images = childContext.product.images;

        if (images && images.length > 0) {
            result = true;
            return false;
        }

        return true;
    });

    return result;
};

const ProductOptions: React.FC<Props> = ({
    variantChildContexts,
    variantTraits,
    selectedVariantId,
    settingsCollection,
    loadRealTimePricing,
    loadRealTimeInventory,
}) => {
    if (!variantTraits || variantTraits.length === 0 || !variantChildContexts || !variantChildContexts.length) {
        return null;
    }

    const productDetailsVariantOptionsContext = useContext(ProductDetailsVariantOptionsContext);
    const pageSize = +(settingsCollection.customSettings as any).plpDefaultPageSize;
    const [pageChanged, setPageChanged] = useState(new Date().getTime());
    const [pagination, setPagination] = useState<ProductOptionPagination>({
        totalItemCount: variantChildContexts.length,
        page: 1,
        pageSize,
        pageSizeOptions: [pageSize, pageSize * 2, pageSize * 3, pageSize * 4],
        filteredChildren: variantChildContexts,
        visibleChildren: variantChildContexts.slice(0, pageSize),
        oneImageAvailable: hasOneAvailableImage(variantChildContexts.slice(0, pageSize)),
        sort: {
            type: "productNumber",
            asc: false,
            data: "",
        },
    });

    const searchOptions = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;

        setPagination(pagination => {
            const tempPagination = {
                ...pagination,
                page: 1,
            };
            const items = sortingMethods[pagination.sort.type](
                variantChildContexts,
                pagination.sort.asc,
                pagination.sort.data,
            );

            if (!newQuery) {
                tempPagination.filteredChildren = items;
            } else {
                tempPagination.filteredChildren = items.filter(
                    childContext =>
                        childContext.product.productNumber.toLowerCase().indexOf(newQuery.toLowerCase()) > -1,
                );
            }

            tempPagination.totalItemCount = tempPagination.filteredChildren.length;
            tempPagination.visibleChildren = tempPagination.filteredChildren.slice(0, pageSize);
            tempPagination.oneImageAvailable = hasOneAvailableImage(tempPagination.visibleChildren);

            return tempPagination;
        });

        setPageChanged(new Date().getTime());
    };

    const changePage = (newPageIndex: number) => {
        const startIndex = (newPageIndex - 1) * pageSize;
        const visibleChildren = pagination.filteredChildren.slice(startIndex, startIndex + pageSize);

        setPagination({
            ...pagination,
            page: newPageIndex,
            visibleChildren,
            oneImageAvailable: hasOneAvailableImage(visibleChildren),
        });
        setPageChanged(new Date().getTime());
    };

    const sortData = (sort: ProductOptionSorting): ProductOptionPagination => {
        const tempPagination = { ...pagination, sort };

        tempPagination.filteredChildren = sortingMethods[sort.type](
            tempPagination.filteredChildren,
            sort.asc,
            sort.data,
        );

        const startIndex = (pagination.page - 1) * pageSize;

        tempPagination.visibleChildren = pagination.filteredChildren.slice(startIndex, startIndex + pageSize);

        return tempPagination;
    };

    const sortByErp = () => {
        setPagination(sortData({ type: "productNumber", asc: !pagination.sort.asc, data: "" }));
        setPageChanged(new Date().getTime());
    };

    const sortByTrait = (traitId: string) => {
        setPagination(sortData({ type: "traitId", asc: !pagination.sort.asc, data: traitId }));
        setPageChanged(new Date().getTime());
    };

    const changeResultsPerPage = () => {};

    const loadPrices = () => {
        const productInfos: ProductInfo[] = pagination.visibleChildren?.map(childContext => childContext.productInfo);

        if (productInfos.length === 0) {
            return;
        }

        loadRealTimePricing({
            productPriceParameters: productInfos,
            onSuccess: result => {
                result.realTimePricingResults?.forEach(pricing => {
                    const childContext = pagination.visibleChildren.find(
                        ({ productInfo }) => productInfo.productId === pricing.productId,
                    );
                    if (childContext) {
                        childContext.productInfo.pricing = pricing;
                        delete childContext.productInfo.failedToLoadPricing;
                    }
                });
                setPagination(cloneDeep(pagination));
            },
            onError: () => {
                pagination.visibleChildren.every(({ productInfo }) => {
                    productInfo.failedToLoadPricing = true;
                    delete productInfo.pricing;
                    return true;
                });
                setPagination(cloneDeep(pagination));
            },
        });

        loadRealTimeInventory({
            productIds: productInfos.map(o => o.productId),
            onSuccess: realTimeInventory => {
                if (realTimeInventory) {
                    realTimeInventory.realTimeInventoryResults?.forEach((inventory: ProductInventoryDto) => {
                        const childContext = pagination.visibleChildren.find(
                            ({ productInfo }) => productInfo.productId === inventory.productId,
                        );
                        if (childContext) {
                            childContext.productInfo = {
                                ...childContext.productInfo,
                                inventory,
                            };
                        }
                    });
                    setPagination(pagination);
                }
            },
        });
    };

    const handleRowClick = (variantValues: [], variantIds: []) => {
        productDetailsVariantOptionsContext.setVariantValues(variantValues);
        productDetailsVariantOptionsContext.setVariantIds(variantIds);
    };

    useEffect(() => {
        sortTraitValuesOfVisibleChildren(variantTraits, pagination.visibleChildren);
        loadPrices();
    }, [pageChanged]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.heading}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>{translate("Product Options")}</Typography>
                    <TextField
                        {...styles.searchInput}
                        onChange={searchOptions}
                        placeholder={translate("Filter Results By EIS Part #")}
                        autoComplete="off"
                        clickableText={translate("search for options")}
                        data-test-selector="productOptionsInputTextField"
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.paginationWrapper}>
                    {pagination.totalItemCount > 0 && (
                        <Typography {...styles.paginationMessage}>
                            {translate("Showing {0} out of").replace("{0}", `${pagination.visibleChildren.length}`)}
                            <b>&nbsp;{pagination.totalItemCount}&nbsp;</b>
                            {translate("options")}
                        </Typography>
                    )}
                    {pagination.totalItemCount > 0 && (
                        <Pagination
                            {...styles.pagination}
                            resultsCount={pagination!.totalItemCount}
                            currentPage={pagination!.page}
                            resultsPerPage={pagination!.pageSize}
                            resultsPerPageOptions={pagination!.pageSizeOptions}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                            {...{ hidePerPage: true }}
                            pageSizeCookie="ProductOptions-PageSize"
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.tableWrapper}>
                <DataTable
                    css={css`
                        position: relative;
                    `}
                >
                    <DataTableHead {...styles.tableHead}>
                        <DataTableHeader {...styles.tableHeaderCell} />
                        {variantTraits?.map(trait => (
                            <DataTableHeader
                                key={trait.id}
                                onSortClick={() => sortByTrait(trait.id)}
                                {...styles.tableHeaderCell}
                            >
                                {trait.nameDisplay}
                            </DataTableHeader>
                        ))}
                        <DataTableHeader onSortClick={() => sortByErp()} {...styles.tableHeaderCell}>
                            {translate("EIS Part #")}
                        </DataTableHeader>
                        <DataTableHeader {...styles.tableHeaderCell} />
                        <DataTableHeader {...styles.tableHeaderCell} />
                    </DataTableHead>
                    <DataTableBody>
                        {pagination.visibleChildren.map(({ product, productInfo }) => (
                            <ProductOptionContext key={product.id} product={product} productInfo={productInfo}>
                                <DataTableRow
                                    {...(selectedVariantId === product.id ? styles.selectedTableRow : styles.tableRow)}
                                    onClick={() => {
                                        const variantValues = [] as any;
                                        const variantIds = [] as any;

                                        variantTraits?.map((value: any) => {
                                            return product.childTraitValues?.map(variant => {
                                                if (variant.styleTraitId === value.id) {
                                                    variantValues.push(variant.id);
                                                    variantIds.push(value.id);
                                                }
                                            });
                                        });

                                        handleRowClick(variantValues, variantIds);
                                    }}
                                >
                                    <DataTableCell {...styles.imageCell}>
                                        <LazyImage
                                            {...styles.image}
                                            src={product.smallImagePath}
                                            altText={product.imageAltText}
                                        />
                                    </DataTableCell>
                                    {variantTraits?.map(value => (
                                        <DataTableCell key={value.id} {...styles.centeredCell}>
                                            <Hidden above="sm">
                                                <b>{value.nameDisplay}:&nbsp;</b>
                                            </Hidden>
                                            {product.childTraitValues?.map(variant => {
                                                if (variant.styleTraitId === value.id) {
                                                    return variant.valueDisplay;
                                                }
                                            })}
                                        </DataTableCell>
                                    ))}
                                    <DataTableCell {...styles.centeredCell}>
                                        {product.productNumber && (
                                            <>
                                                <Hidden above="sm">
                                                    <b>{translate("EIS Part #")}:&nbsp;</b>
                                                </Hidden>
                                                {product.productNumber}
                                            </>
                                        )}
                                        {product.customerProductNumber && (
                                            <Typography as="p">
                                                <Typography>{translate("My Part #")}:</Typography>
                                                {product.customerProductNumber}
                                            </Typography>
                                        )}
                                    </DataTableCell>
                                    <DataTableCell {...styles.inventoryCell}>
                                        {settingsCollection.productSettings.showInventoryAvailability && (
                                            <ProductContextAvailability
                                                extendedStyles={styles.availabilityExtendedStyles}
                                            />
                                        )}
                                    </DataTableCell>
                                    <DataTableCell {...styles.actionsCell}>
                                        <ProductContext.Consumer>
                                            {value => (
                                                <ProductPrice
                                                    showLabel={false}
                                                    product={value}
                                                    extendedStyles={styles.priceExtendedStyles}
                                                />
                                            )}
                                        </ProductContext.Consumer>
                                        <StyledWrapper {...styles.productActionsWrapper}>
                                            <ProductQuantityOrdered
                                                extendedStyles={styles.extendedQtyFieldStyles}
                                                labelOverride=""
                                                id={`${product.id}-qty`}
                                            />
                                            <ProductAddToCartButton
                                                extendedStyles={styles.extendedAddToCartStyles}
                                                {...({ iconOnly: true } as any)}
                                                data-test-selector={`actionsAddToCart${product.id}`}
                                            />
                                            <Popover
                                                {...styles.qtyPopover}
                                                wrapperProps={styles.qtyPopoverWrapper}
                                                contentBodyProps={styles.qtyPopoverContentBody as any}
                                                popoverTrigger={<Icon src={Info} {...styles.qtyPopoverIcon} />}
                                            >
                                                <Typography {...styles.valueText} variant="p">
                                                    <Typography {...styles.labelText}>
                                                        {translate("Min Qty:")}
                                                    </Typography>
                                                    <Typography>{product.minimumOrderQty || 1}</Typography>
                                                </Typography>
                                                <Typography {...styles.valueText} variant="p">
                                                    <Typography {...styles.labelText}>
                                                        {translate("Qty Increment:")}
                                                    </Typography>
                                                    <Typography>{product.detail?.multipleSaleQty || 1}</Typography>
                                                </Typography>
                                            </Popover>
                                        </StyledWrapper>
                                    </DataTableCell>
                                </DataTableRow>
                            </ProductOptionContext>
                        ))}
                        {pagination.visibleChildren.length === 0 && (
                            <DataTableRow>
                                <DataTableCell colSpan={4 + variantTraits.length}>
                                    {translate("No Options Found.")}
                                </DataTableCell>
                            </DataTableRow>
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            {pagination.totalItemCount > 0 && (
                <Pagination
                    {...styles.pagination}
                    resultsCount={pagination!.totalItemCount}
                    currentPage={pagination!.page}
                    resultsPerPage={pagination!.pageSize}
                    resultsPerPageOptions={pagination!.pageSizeOptions}
                    onChangePage={changePage}
                    onChangeResultsPerPage={changeResultsPerPage}
                    {...{ hidePerPage: true }}
                    pageSizeCookie="ProductOptions-PageSize"
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, mapDispatchToProps)(ProductOptions)),
    definition: {
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
